import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Target, Award, Church, Zap, TrendingUp } from 'lucide-react';

const AboutUs = () => {
  const companies = [
    {
      name: 'Sygmatec Smart Solutions Private Limited',
      focus: 'Smart Temples',
      icon: Church,
      description: 'Pioneering the integration of cutting-edge technology with spiritual heritage. We specialize in transforming traditional temples into smart ecosystems that enhance devotee experience while preserving sacred traditions.',
      solutions: [
        'Intelligent Facade Lighting Systems',
        'Smart Crowd Management',
        'Digital Donation Tracking',
        'Video Analytics with Face Recognition',
        'Automated Parking Management (ANPR)',
        'Command & Control Centers'
      ],
      color: '#00FFD1'
    },
    {
      name: 'HS-Sygmatec Private Limited',
      focus: 'Smart Energy & Buildings',
      icon: Zap,
      description: 'Leading the transformation of energy infrastructure and commercial buildings through IoT-enabled digitization. We create intelligent, sustainable solutions for modern grid management and building automation.',
      solutions: [
        'Transformer Digitization with IoT Sensors',
        'GIS-Based Asset Mapping',
        'Real-Time Energy Monitoring Portals',
        'ICCC for VVIP Buildings',
        'CBRN Detection Systems',
        'Integrated Building Management'
      ],
      color: '#00FFD1'
    },
    {
      name: 'Sygmatec Ventures Private Limited',
      focus: 'Smart Stadiums',
      icon: TrendingUp,
      description: 'Revolutionizing sports and entertainment venues by transforming stadiums into intelligent, multi-purpose "Sports Cities" that deliver exceptional fan experiences and operational excellence.',
      solutions: [
        'Fan Engagement Platforms (AR/VR)',
        'Contactless Entry Systems',
        'AI-Powered Operations & Maintenance',
        'Smart Parking & Transportation',
        'Broadcasting Integration',
        'Multi-Purpose Venue Transformation'
      ],
      color: '#00FFD1'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Innovation',
      description: 'We push the boundaries of technology to create solutions that transform industries and improve lives.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Working closely with clients, partners, and stakeholders to deliver integrated solutions that exceed expectations.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to the highest standards of quality, reliability, and performance in everything we do.'
    },
    {
      icon: Building2,
      title: 'Sustainability',
      description: 'Building smart solutions that reduce environmental impact and promote sustainable growth for future generations.'
    }
  ];

  return (
    <div className="about-us-page">
      {/* Hero Section */}
      <div className="about-hero-section">
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <Link to="/" className="back-button-detail">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="about-hero-title">About Sygmatec</h1>
          <p className="about-hero-subtitle">
            Empowering the Future Through Smart Technology Solutions
          </p>
        </div>
      </div>

      <div className="about-container">
        {/* Mission Section */}
        <section className="about-section">
          <div className="about-mission-container">
            <h2 className="about-section-title">Our Mission</h2>
            <p className="about-mission-text">
              At Sygmatec, we are committed to driving digital transformation across diverse sectors 
              through innovative smart solutions. Our mission is to create safer, more efficient, and 
              hyper-connected environments that improve quality of life while respecting tradition and 
              promoting sustainability.
            </p>
            <p className="about-mission-text">
              By merging cutting-edge hardware with intelligent software, we enable organizations and 
              urban planners to seamlessly transition into the "smart" era with comprehensive, integrated 
              solutions backed by actionable insights.
            </p>
          </div>
        </section>

        {/* Our Companies Section */}
        <section className="about-section companies-section">
          <h2 className="about-section-title">Our Companies</h2>
          <p className="about-companies-intro">
            Sygmatec operates through specialized entities, each focusing on distinct solution domains 
            to deliver unparalleled expertise and tailored solutions.
          </p>
          
          <div className="companies-grid">
            {companies.map((company, index) => (
              <div key={index} className="company-card">
                <div className="company-header">
                  <div className="company-icon-container">
                    <company.icon size={32} />
                  </div>
                  <div className="company-header-text">
                    <h3 className="company-name">{company.name}</h3>
                    <p className="company-focus">{company.focus}</p>
                  </div>
                </div>
                
                <p className="company-description">{company.description}</p>
                
                <div className="company-solutions">
                  <h4 className="solutions-heading">Core Solutions:</h4>
                  <ul className="solutions-list">
                    {company.solutions.map((solution, idx) => (
                      <li key={idx}>{solution}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Our Values Section */}
        <section className="about-section">
          <h2 className="about-section-title">Our Values</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon-container">
                  <value.icon size={32} />
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Approach Section */}
        <section className="about-section">
          <h2 className="about-section-title">Our Approach</h2>
          <div className="approach-container">
            <div className="approach-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Understand</h3>
                <p>Deep dive into client needs, challenges, and operational context to design tailored solutions.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Innovate</h3>
                <p>Leverage cutting-edge technologies and industry best practices to create intelligent solutions.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Integrate</h3>
                <p>Seamlessly integrate systems and ensure smooth deployment with minimal disruption.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3>Optimize</h3>
                <p>Continuously monitor, analyze, and enhance solutions to deliver sustained value.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-section about-cta-section">
          <div className="about-cta-container">
            <h2 className="about-cta-title">Ready to Transform Your Operations?</h2>
            <p className="about-cta-text">
              Partner with Sygmatec to unlock the full potential of smart technology solutions 
              tailored to your unique needs.
            </p>
            <Link to="/#solutions" className="btn-primary">
              Explore Our Solutions
              <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
